import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import "./MolGrid.scss";
import PropTypes from "prop-types";
import MolGridHeader from "./MolGrid-Header";
import MolGridDataRows from "./Molgrid-data-row";
import MolPagination from "./Pagination/MolPagination";
import DataLoader from "../ui/dataLoader/DataLoader";
import NoRecordFound from "../ui/noRecordFound/NoRecordFound";

const MolGrid = forwardRef((props, ref) => {
  // Initialize state for currentPage, pageSize, and totalPageCount
  const [currentPage, setCurrentPage] = useState(
    props.pagination?.currentPage || 1
  );
  const [pageSize, setPageSize] = useState(props.pagination?.pageSize || 10);
  const [totalPageCount, setTotalPageCount] = useState(1);
  const [shorting, setShorting] = useState([{
    fieldName: "name",
    isAce: true,
    shortNumber: 1
  }]);
  // Calculate the total page count whenever totalCount or pageSize changes
  useEffect(() => {
    if (props.pagination.totalCount > 0) {
      calctotalPage(pageSize);
    }
  }, [props.pagination.totalCount, pageSize]);

  // Calculate total page count and adjust currentPage if necessary
  const calctotalPage = (newPageSize) => {
    let newTotalPageCount = Math.ceil(
      props.pagination.totalCount / newPageSize
    );
    if (newTotalPageCount === 0) {
      newTotalPageCount = 1;
    }
    if (currentPage > newTotalPageCount) {
      setCurrentPage(newTotalPageCount);
    }
    setTotalPageCount(newTotalPageCount);
  };

  // Get the default page object
  const getDefaultPageObject = () => {
    setCurrentPage(1);
    return {
      pageNumber: 1,
      pageSize: pageSize,
      // Add other pagination-related properties here
    };
  };

  // Get the current page object
  const getCurrentPageObject = () => {
    return {
      pageNumber: currentPage,
      pageSize: pageSize,
      // Add other pagination-related properties here
    };
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (props.onPageChange) {
      props.onPageChange({
        pageNumber: newPage,
        pageSize: pageSize,
        // Add other pagination-related properties here
      });
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    calctotalPage(newSize);
    setCurrentPage(1);
    if (props.onPageChange) {
      props.onPageChange({
        pageNumber: 1,
        pageSize: newSize,
        // Add other pagination-related properties here
      });
    }
  };

  const generateShortString = () => {
    return shorting.map(item => {
      const sortingOrder = item.isAce ? '' : ' desc';
      return `${item.fieldName}${sortingOrder}`;
    }).join(',');
  }
  const handleShorting = (fieldName) => {
    const index = shorting.findIndex(item => item.fieldName === fieldName);

    if (index !== -1) {
      // If it exists, toggle the 'isAce' property
      const updatedShorting = [...shorting];
      updatedShorting[index] = {
        ...updatedShorting[index],
        isAce: !updatedShorting[index].isAce
      };
      setShorting(updatedShorting);
    } else {
      // Calculate the next shortNumber
      const nextShortNumber = shorting.length + 1;

      // Create a new object with the calculated shortNumber
      const newItem = {
        fieldName,
        isAce: true,
        shortNumber: nextShortNumber
      };

      setShorting([...shorting, newItem]);
    }

  }
  // Use useImperativeHandle to make the getCurrentPageObject function accessible via the ref
  useImperativeHandle(ref, () => ({
    getCurrentPageObject,
    getDefaultPageObject,
    generateShortString
  }));

  const renderDataRow = (prop) => {
    const isRenderRow =
      prop.dataSource && prop.dataSource.length > 0 ? true : false;
    return (
      <>
        {prop.isLoading ? (
          //TODO :PC : changes add new component
          <td colSpan={100}>
            <DataLoader />
          </td>
        ) : isRenderRow ? (
          <MolGridDataRows
            dataSource={prop.dataSource}
            columns={prop.configuration.columns}
            onActionChange={prop.onActionChange}
          />
        ) : (
          //TODO :PC : changes add new component
          <tr>
            <td className="record-not-found-td" colSpan={100}>
              <NoRecordFound />
            </td>
          </tr>
        )}
      </>
    );
  };

  return (
    <div>
      <table className="border-table-simple">
        <thead>
          <MolGridHeader columns={props.configuration.columns} onShorting={handleShorting} selectedShorting={shorting} />
        </thead>
        <tbody>{renderDataRow(props)}</tbody>
      </table>
      {props.allowPagination && (
        <MolPagination
          pageSize={pageSize}
          currentPage={currentPage}
          totalPages={totalPageCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
});

MolGrid.propTypes = {
  configuration: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  dataSource: PropTypes.arrayOf(PropTypes.object),
  allowPagination: PropTypes.bool,
  pagination: PropTypes.shape({
    currentPage: PropTypes.number,
    totalCount: PropTypes.number,
    pageSize: PropTypes.number,
  }),
};

MolGrid.defaultProps = {
  allowPagination: true,
  pagination: {
    currentPage: 1,
    totalCount: 1,
    pageSize: 10,
  },
};

export default MolGrid;
